import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { useKantataProjects, useTherapyAreas } from "@/hooks/useMasterData";
import { masterDataApi } from "@/services/masterDataApi";
import { useUsers } from "@/hooks/useUsers";
import { cn } from "@/lib/utils";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  AiPermissionsSection,
  SupportingDocumentsSection,
  areSectionsValid,
  isSectionEnabled,
  isSectionRequired,
  useAiPermissionOptions,
  useAppSections,
  type PendingSupportingDocument,
} from "@lumanity/ui/projects";

export interface ProjectFormValues {
  name: string;
  kantataProjectId: string;
  code: string;                 // derived from selected Kantata project — sent to API
  kantataProjectName?: string;  // display label only — not sent to API
  clientIds: string[];          // FKs to netsuite_clients — derived from selected Kantata project
  clientNames?: string[];       // display labels only — not sent to API directly
  therapyAreas: string[];
  description: string;
  memberIds: string[];
  model: string;
  isAdvancedRag: boolean;
  /** Optional sections (create only): AI permission keys confirmed, files to upload once the project exists. */
  aiPermissions: string[];
  pendingSupportingDocuments: PendingSupportingDocument[];
}

export const PROJECT_MODEL_OPTIONS: { value: string; label: string }[] = [
  { value: "opus-5", label: "Claude Opus 5" },
  { value: "sonnet-5", label: "Claude Sonnet 5" },
  { value: "opus-48", label: "Claude Opus 4.8" },
  { value: "opus-46", label: "Claude Opus 4.6" },
  { value: "sonnet-46", label: "Claude Sonnet 4.6" },
  // { value: "gpt-52", label: "GPT-5.2" },
  { value: "gpt-53-codex", label: "GPT-5.3-codex" },
];

export const PROJECT_MODEL_DEFAULT = "opus-5";

interface ProjectFormProps {
  defaultValues?: Partial<ProjectFormValues> & { isAdvancedRag?: boolean };
  /**
   * Owner (creator) of an existing project. Omit when creating a new project —
   * the current user becomes the owner and is added to the team automatically.
   * When editing, the owner is whoever created the project, never the person
   * looking at the form: an admin editing someone else's project must add
   * themselves to the team explicitly.
   */
  ownerId?: string | null;
  /** Existing project: its optional sections are shown live (files upload straight away, permissions read-only). */
  projectId?: string;
  onSubmit: (values: ProjectFormValues) => void;
  isSubmitting: boolean;
  submitLabel: string;
  onCancel?: () => void;
}

const ProjectForm = ({
  defaultValues = {},
  ownerId,
  projectId,
  onSubmit,
  isSubmitting,
  submitLabel,
  onCancel,
}: ProjectFormProps) => {
  const { user: currentUser } = useAuth();
  const { data: users = [] } = useUsers();
  const { data: therapyAreas = [] } = useTherapyAreas();
  const queryClient = useQueryClient();
  const [name, setName] = useState(defaultValues.name ?? "");
  const [kantataProjectId, setKantataProjectId] = useState(defaultValues.kantataProjectId ?? "");
  const [code, setCode] = useState(defaultValues.code ?? "");
  const [selectedKantataCode, setSelectedKantataCode] = useState(defaultValues.kantataProjectName ?? "");
  const [kantataPopoverOpen, setKantataPopoverOpen] = useState(false);
  const [kantataInputValue, setKantataInputValue] = useState("");
  const [kantataSearch, setKantataSearch] = useState("");
  const kantataDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { data: kantataResults = [], isFetching: kantataFetching } = useKantataProjects(kantataSearch);
  const [clientIds, setClientIds] = useState<string[]>(defaultValues.clientIds ?? []);
  const [clientNames, setClientNames] = useState<string[]>(defaultValues.clientNames ?? []);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [selectedTherapyAreas, setSelectedTherapyAreas] = useState<string[]>(defaultValues.therapyAreas ?? []);
  const [therapyAreaPopoverOpen, setTherapyAreaPopoverOpen] = useState(false);
  const [description, setDescription] = useState(defaultValues.description ?? "");
  const [model, setModel] = useState(defaultValues.model ?? PROJECT_MODEL_DEFAULT);
  const [isAdvancedRag, setIsAdvancedRag] = useState(defaultValues.isAdvancedRag ?? false);
  const [memberIds, setMemberIds] = useState<string[]>(defaultValues.memberIds ?? []);
  const [memberPopoverOpen, setMemberPopoverOpen] = useState(false);
  const [aiPermissions, setAiPermissions] = useState<string[]>([]);
  const [pendingDocuments, setPendingDocuments] = useState<PendingSupportingDocument[]>([]);

  // Optional sections come from the app's config in the project service. If it can't be
  // reached (e.g. the project service is turned off), show only the core sections.
  const sectionsQuery = useAppSections();
  const sections = sectionsQuery.isError ? [] : sectionsQuery.data;
  const showDocuments = isSectionEnabled(sections, "supporting_documents");
  const showAiPermissions = isSectionEnabled(sections, "ai_permissions");
  const { data: aiPermissionOptions } = useAiPermissionOptions({ enabled: showAiPermissions && !projectId });
  // No ownerId prop means we're creating a project, so the current user owns it.
  const isNewProject = ownerId === undefined;

  // Only creation is gated: confirmations are recorded once, when the project is created.
  const sectionsValid = projectId !== undefined || areSectionsValid(sections, { aiPermissionOptions, aiPermissions, supportingDocuments: pendingDocuments.length });
  const canSubmit = Boolean(name && (kantataProjectId || (!isNewProject && code))) && sectionsValid;

  const currentApiUser = useMemo(
    () => users.find((u) => u.email === currentUser?.email),
    [users, currentUser]
  );

  const ownerUserId = isNewProject ? currentApiUser?.id : ownerId ?? undefined;

  useEffect(() => {
    if (!isNewProject) return;
    if (currentApiUser && !memberIds.includes(currentApiUser.id)) {
      setMemberIds((prev) => [currentApiUser.id, ...prev]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentApiUser?.id, isNewProject]);

  // In edit mode, resolve the Kantata project display name.
  // Client names are seeded directly from defaultValues (read-only field — no re-fetch needed).
  useEffect(() => {
    if (isNewProject || !code) return;
    let cancelled = false;

    if (kantataProjectId) {
      // Fast path: ID already known from stored FK — fetch name for display
      queryClient
        .fetchQuery({
          queryKey: ["master", "kantata-projects", "by-id", kantataProjectId],
          queryFn: () => masterDataApi.kantataProjectById(kantataProjectId),
          staleTime: 300_000,
        })
        .then((kp) => { if (!cancelled) setSelectedKantataCode(kp.name); })
        .catch(() => {});
    } else {
      // Fallback for old projects that predate the kantata_project_id column
      queryClient
        .fetchQuery({
          queryKey: ["master", "kantata-projects", "search-by-code", code],
          queryFn: () => masterDataApi.kantataProjects(code, 10),
          staleTime: 300_000,
        })
        .then((projects) => {
          if (cancelled) return;
          const match = projects.find((p) => p.code === code);
          if (!match) return;
          setKantataProjectId(match.id);
          setSelectedKantataCode(match.name);
        })
        .catch(() => {});
    }

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNewProject, code]); // intentionally excludes kantataProjectId from deps

  const sortedUsers = useMemo(() => {
    return [...users].sort((a, b) => {
      const aAdmin = a.role.toLowerCase() === "admin";
      const bAdmin = b.role.toLowerCase() === "admin";
      if (aAdmin !== bAdmin) return aAdmin ? -1 : 1;
      return a.email.localeCompare(b.email);
    });
  }, [users]);

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit({
      name,
      kantataProjectId,
      code,
      kantataProjectName: selectedKantataCode,
      clientIds,
      clientNames,
      therapyAreas: selectedTherapyAreas,
      description,
      memberIds,
      model,
      isAdvancedRag,
      aiPermissions: showAiPermissions ? aiPermissions : [],
      pendingSupportingDocuments: showDocuments ? pendingDocuments : [],
    });
  };

  return (
    <>
      {/* Project Identification */}
      <section className="mb-8">
        <h2 className="text-lg font-semibold text-brand-navy mb-4">Project Identification</h2>
        <div className="bg-card border rounded-lg p-6 space-y-5">
          <div>
            <Label className="font-semibold">Project Name <span className="text-destructive">*</span></Label>
            <Input placeholder="e.g., Ex-US Lupus Market Map" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="font-semibold">Project Code (Kantata) <span className="text-destructive">*</span></Label>
              <Popover
                open={kantataPopoverOpen}
                onOpenChange={(open) => {
                  setKantataPopoverOpen(open);
                  if (!open) {
                    setKantataInputValue("");
                    setKantataSearch("");
                    if (kantataDebounceRef.current) clearTimeout(kantataDebounceRef.current);
                  }
                }}
              >
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={kantataPopoverOpen}
                    className="mt-1.5 w-full justify-between font-normal text-muted-foreground hover:text-muted-foreground hover:bg-inherit"
                  >
                    <span className="truncate">
                      {selectedKantataCode ? selectedKantataCode : "Select Kantata project code..."}
                    </span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0" align="start" style={{ width: "var(--radix-popover-trigger-width)" }}>
                  <Command shouldFilter={false}>
                    <CommandInput
                      placeholder="Search by code or name..."
                      value={kantataInputValue}
                      onValueChange={(val) => {
                        setKantataInputValue(val);
                        if (kantataDebounceRef.current) clearTimeout(kantataDebounceRef.current);
                        kantataDebounceRef.current = setTimeout(() => setKantataSearch(val), 350);
                      }}
                    />
                    <CommandList>
                      {kantataFetching && <CommandEmpty>Loading...</CommandEmpty>}
                      {!kantataFetching && kantataResults.length === 0 && (
                        <CommandEmpty>No Kantata projects found.</CommandEmpty>
                      )}
                      {!kantataFetching && kantataResults.length > 0 && (
                        <CommandGroup>
                          {kantataResults.map((project) => (
                            <CommandItem
                              key={project.id}
                              value={project.id}
                              onSelect={() => {
                                if (project.id === kantataProjectId) {
                                  setKantataProjectId("");
                                  setSelectedKantataCode("");
                                  setCode("");
                                  setClientIds([]);
                                  setClientNames([]);
                                } else {
                                  setKantataProjectId(project.id);
                                  setSelectedKantataCode(project.name);
                                  setCode(project.code ?? "");
                                  setClientIds([]);
                                  setClientNames([]);
                                  setClientsLoading(true);
                                  queryClient
                                    .fetchQuery({
                                      queryKey: ["master", "kantata-projects", "by-id", project.id, "clients"],
                                      queryFn: () => masterDataApi.kantataProjectClients(project.id),
                                      staleTime: 300_000,
                                    })
                                    .then((clients) => {
                                      setClientIds(clients.map((c) => c.id));
                                      setClientNames(clients.map((c) => c.name));
                                    })
                                    .catch(() => { setClientIds([]); setClientNames([]); })
                                    .finally(() => setClientsLoading(false));
                                }
                                setKantataPopoverOpen(false);
                              }}
                            >
                              <Check className={cn("mr-2 h-4 w-4 shrink-0", kantataProjectId === project.id ? "opacity-100" : "opacity-0")} />
                              <span className="truncate">{project.name}</span>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      )}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <Label className="font-semibold">Client Name</Label>
              {/* Read-only — derived from the selected Kantata project, no manual selection */}
              <div className="mt-1.5 min-h-9 w-full rounded-md border border-dashed bg-muted/50 px-3 py-1.5 cursor-not-allowed">
                {clientsLoading ? (
                  <span className="text-sm text-muted-foreground">Loading clients...</span>
                ) : clientNames.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {clientNames.map((name, i) => (
                      <Badge key={clientIds[i] ?? name} variant="secondary" className="pointer-events-none">
                        {name}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <span className="text-sm text-muted-foreground">
                    {kantataProjectId ? "No clients associated with this code" : "Select a Kantata code to populate clients"}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="font-semibold">Model <span className="text-destructive">*</span></Label>
              <Select value={model} onValueChange={setModel}>
                <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select model..." /></SelectTrigger>
                <SelectContent>
                  {PROJECT_MODEL_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end pb-0.5">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="is-advanced-rag"
                  checked={isAdvancedRag}
                  onCheckedChange={(checked) => setIsAdvancedRag(!!checked)}
                  disabled={!isNewProject}
                />
                <Label
                  htmlFor="is-advanced-rag"
                  className={cn("font-semibold cursor-pointer", !isNewProject && "opacity-60 cursor-not-allowed")}
                >
                  Is Advanced RAG
                </Label>
              </div>
            </div>
          </div>
          <div>
            <Label className="font-semibold">Project Description / Scope Statement</Label>
            <Textarea
              placeholder="Describe the project scope and intent..."
              className="mt-1.5 min-h-[100px]"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
        </div>
      </section>

      {/* Clinical Information */}
      <section className="mb-8">
        <h2 className="text-lg font-semibold text-brand-navy mb-4">Clinical Information</h2>
        <div className="bg-card border rounded-lg p-6 space-y-5">
          <div>
            <Label className="font-semibold">Therapy Area/s (MeSH)</Label>
            <Popover open={therapyAreaPopoverOpen} onOpenChange={setTherapyAreaPopoverOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={therapyAreaPopoverOpen}
                  className="mt-1.5 w-full justify-between font-normal text-muted-foreground hover:text-muted-foreground hover:bg-inherit"
                >
                  {selectedTherapyAreas.length > 0
                    ? `${selectedTherapyAreas.length} therapy area${selectedTherapyAreas.length > 1 ? "s" : ""} selected`
                    : "Select therapy area/s..."}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-full p-0" align="start" style={{ width: "var(--radix-popover-trigger-width)" }}>
                <Command>
                  <CommandInput placeholder="Search therapy areas..." />
                  <CommandList>
                    <CommandEmpty>No therapy areas found.</CommandEmpty>
                    <CommandGroup>
                      {therapyAreas.map((t) => {
                        const isSelected = selectedTherapyAreas.includes(t.name);
                        return (
                          <CommandItem
                            key={t.id}
                            value={t.name}
                            onSelect={() => {
                              setSelectedTherapyAreas((prev) =>
                                isSelected ? prev.filter((v) => v !== t.name) : [...prev, t.name]
                              );
                            }}
                          >
                            <Check className={cn("mr-2 h-4 w-4", isSelected ? "opacity-100" : "opacity-0")} />
                            {t.name}
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {selectedTherapyAreas.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {selectedTherapyAreas.map((name) => (
                  <Badge key={name} variant="secondary" className="gap-1 pr-1">
                    {name}
                    <button
                      type="button"
                      aria-label={`Remove ${name}`}
                      onClick={() => setSelectedTherapyAreas((prev) => prev.filter((v) => v !== name))}
                      className="rounded-full hover:bg-muted p-0.5"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Project Team */}
      <section className="mb-8">
        <h2 className="text-lg font-semibold text-brand-navy mb-4">Project Team</h2>
        <div className="bg-card border rounded-lg p-6">
          <Label className="font-semibold">Project Team Members (Active Directory)</Label>
          <Popover open={memberPopoverOpen} onOpenChange={setMemberPopoverOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={memberPopoverOpen}
                className="mt-1.5 w-full justify-between font-normal text-muted-foreground hover:text-muted-foreground hover:bg-inherit"
              >
                {memberIds.length > 0
                  ? `${memberIds.length} member${memberIds.length > 1 ? "s" : ""} selected`
                  : "Select team members from Active Directory..."}
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-full p-0" align="start" style={{ width: "var(--radix-popover-trigger-width)" }}>
              <Command>
                <CommandInput placeholder="Search members..." />
                <CommandList>
                  <CommandEmpty>No members found.</CommandEmpty>
                  <CommandGroup>
                    {sortedUsers.map((user) => {
                      const isSelected = memberIds.includes(user.id);
                      const isOwner = !!ownerUserId && user.id === ownerUserId;
                      return (
                        <CommandItem
                          key={user.id}
                          value={user.email}
                          onSelect={() => {
                            if (isOwner) return;
                            setMemberIds((prev) =>
                              isSelected ? prev.filter((v) => v !== user.id) : [...prev, user.id]
                            );
                          }}
                          className={cn(isOwner && "pointer-events-none opacity-60")}
                        >
                          <Check className={cn("mr-2 h-4 w-4", isSelected ? "opacity-100" : "opacity-0")} />
                          {user.email}
                          {isOwner && <span className="ml-auto text-xs text-muted-foreground">owner</span>}
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          {memberIds.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {memberIds.map((memberId) => {
                const user = users.find((u) => u.id === memberId);
                if (!user) return null;
                return (
                  <Badge key={memberId} variant="secondary" className="gap-1 pr-1">
                    {user.email}
                    {memberId !== ownerUserId ? (
                      <button
                        type="button"
                        aria-label={`Remove ${user.email}`}
                        onClick={() => setMemberIds((prev) => prev.filter((v) => v !== memberId))}
                        className="rounded-full hover:bg-muted p-0.5"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    ) : (
                      <span className="px-0.5 text-xs text-muted-foreground">owner</span>
                    )}
                  </Badge>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {showDocuments && (
        <SupportingDocumentsSection
          projectId={projectId}
          pending={pendingDocuments}
          onPendingChange={setPendingDocuments}
          required={isSectionRequired(sections, "supporting_documents")}
          onError={(e) => toast.error(e.message)}
        />
      )}
      {showAiPermissions && (
        <AiPermissionsSection
          projectId={projectId}
          value={aiPermissions}
          onChange={setAiPermissions}
          required={isSectionRequired(sections, "ai_permissions")}
        />
      )}

      <div className="flex justify-end gap-3">
        {onCancel && (
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button
          className="bg-brand-teal text-white hover:bg-brand-teal/90 px-8"
          onClick={handleSubmit}
          disabled={isSubmitting || !canSubmit}
        >
          {isSubmitting ? "Saving..." : submitLabel}
        </Button>
      </div>
    </>
  );
};

export default ProjectForm;
